import React, {
  ReactElement,
  useEffect,
  useState,
  useCallback,
  useReducer,
} from 'react';
import { SchemaWithClient } from '../../types/SchemaWithClient';
import { Entity } from '../../types/entities/Entity';
import {
  Button as MUIButton,
  IconButton,
  Stack,
  Table,
  TableContainer,
  Typography,
} from '@mui/material';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import { SlidingPaneEdit } from '../Page/SlidingPaneEdit';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import api from '../../services/api';
import { SlidingPaneAddNew } from '../Page/SlidingPaneAddNew';
import CloudDoneRoundedIcon from '@mui/icons-material/CloudDoneRounded';
import Fade from '@mui/material/Fade';
import CircularProgress from '@mui/material/CircularProgress';
import { debounce } from 'lodash';

import Paper from '@mui/material/Paper';
import { CollapseRow } from '../Table/row/CollapseRow';
import { accommodationReservation } from '../../schema/accommodationReservation';
import toast from "react-hot-toast";
import {RefreshRounded} from "@mui/icons-material";
import {useStateContext} from "../../hooks/useStateContext";
import {hasPermissionByPriority} from "../../core/permissions/hasPermission";
import {ROLE_PRIORITIES} from "../../core/permissions/permissions";

type Props<T extends Entity> = {
  schema: SchemaWithClient<T>;
};

const reducer = (state: any, action: any) => {
  switch (action.type) {
    case 'SET_INPUT':
      const newCurrent: any = [...state.current];

      return {
        ...state,
        current: newCurrent,
        send: true,
      };
    case 'SET_HISTORY':
      return {
        ...state,
        current: action.payload,
        send: false,
      };
    default:
      return state;
  }
};

export const AccommodationReservation = ({
  schema,
  values,
  fullWidth,
  refreshMainTable,
}: {
  schema: any;
  values: any;
  fullWidth: boolean;
  refreshMainTable?: () => void;
}): ReactElement => {
  const [openConfirmDialog, setOpenConfirmDialog] = useState<any>(false);
  const [state, dispatch] = useReducer(reducer, {
    current: [],
    send: false,
  });
  const [openedSlidingPane, setOpenedSlidingPane] = useState<any>({});
  const [query, setQuery] = React.useState<'idle' | 'progress' | 'success'>(
    'idle'
  );
  const timerRef = React.useRef<ReturnType<typeof setTimeout>>();
  const {user} = useStateContext();
  const isAdmin = hasPermissionByPriority(user.roles, ROLE_PRIORITIES[1]);

  const [currentAddNewPaneEntity, setCurrentAddNewPaneEntity] = React.useState<
    any
  >({
    schema: schema,
    row: {},
  });

  const fetchMainData = useCallback(() => {
    const searchParams = new URLSearchParams({});

    api
      .get(`/groups/${values.id}/accommodation-reservations`, searchParams)
      .then((res: any) => {
        dispatch({
          type: 'SET_HISTORY',
          payload: res,
        });
      });
  }, []);

  useEffect(() => {
    fetchMainData();
  }, []);

  const debouncedChange = useCallback(
    debounce(() => {
      /* if (state.send) {
        onChange(state.current, 'corporates');
      }*/
    }, 500),
    [state]
  );

  useEffect(() => {
    debouncedChange();
  }, [state, debouncedChange]);

  const handleRestore = (row:any)=> {

    accommodationReservation().client
      .restore(row.id,{})
      .then((result: any) => {
        fetchMainData();
        toast.success(`Successfully restored Accommodation Reservation`);

      })
      .catch((error: any) => {
        toast.error(error?.message);
      });

  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table aria-label="simple table" size="small" sx={{}}>
          <TableHead
            sx={{
              '& .MuiTableCell-root': {
                color: 'inherit',
                border: 'none',
              },
              '& .MuiTableRow-root .MuiTableCell-root.MuiTableCell-head': {
                border: 'none',
                fontSize: '14px',
                p: '10px',
              },
              background: '#256861',
              color: '#fff',
            }}
          >
            <TableRow
              sx={{
                '& .MuiTableCell-root': {
                  overflowWrap: 'anywhere',
                },
              }}
            >
              <TableCell sx={{}}></TableCell>
              <TableCell sx={{}}>Accommodation</TableCell>
              {fullWidth && (
                <>
                  <TableCell
                    sx={{
                      textAlign: 'left',
                    }}
                  >
                    Type
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: 'center',
                    }}
                  >
                    Status
                  </TableCell>

                  <TableCell
                    sx={{
                      textAlign: 'left',
                    }}
                  >
                    Address
                  </TableCell>
                </>
              )}
              <TableCell
                sx={{
                  textAlign: 'left',
                }}
              >
                Arrival / Departure
              </TableCell>
              <TableCell
                sx={{
                  textAlign: 'right',
                }}
              >
                Size
              </TableCell>
              <TableCell sx={{}}></TableCell>
            </TableRow>
          </TableHead>
          {state.current.map((accommodationItem: any) => {
            return (
              <CollapseRow
                row={accommodationItem}
                fullWidth={fullWidth}
                clickOnEdit={(item: any) => {
                  setOpenedSlidingPane({
                    schema: accommodationReservation(),
                    row: item,
                  });
                }}
                clickOnRestore={(item: any) => {
                  handleRestore(item);
                }}
                clickOnDelete={(item: any) => {
                  setOpenConfirmDialog({
                    row: item,
                    schema: accommodationReservation(),
                  });
                }}
              />
            );
          })}
        </Table>
      </TableContainer>

      <Stack
        direction={'row'}
        alignItems={'center'}
        sx={{
          width: '100%',
        }}
        justifyContent={'space-between'}
      >
        <Stack direction={'row'}>
          <Tooltip
            title={`Add new`}
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, -14],
                    },
                  },
                ],
              },
            }}
          >
            <IconButton
              color={'primary'}
              onClick={() => {
                setCurrentAddNewPaneEntity({
                  row: {
                    id: 'new',
                    group: {
                      id: values?.id,
                      name: values?.name,
                    },
                  },
                  schema: accommodationReservation(),
                });
              }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={`Refresh data`}
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, -14],
                    },
                  },
                ],
              },
            }}
          >
            <IconButton
              color={'primary'}
              onClick={() => {
                fetchMainData();
              }}
            >
              <RefreshRounded />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack>
          {query === 'success' ? (
            <Stack direction={'row'} alignItems={'center'}>
              <CloudDoneRoundedIcon color={'disabled'} />
              <Typography variant={'caption'} marginLeft={'5px'} color={'gray'}>
                successfully saved
              </Typography>
            </Stack>
          ) : (
            <Fade
              in={query === 'progress'}
              style={{
                transitionDelay: query === 'progress' ? '800ms' : '0ms',
              }}
              unmountOnExit
            >
              <CircularProgress size={20} />
            </Fade>
          )}
        </Stack>
      </Stack>

      {currentAddNewPaneEntity?.row?.id && (
        <>
          <SlidingPaneAddNew
            schema={currentAddNewPaneEntity?.schema}
            mainSchema={schema}
            id={currentAddNewPaneEntity?.row?.id}
            hasSlidingPane={false}
            onSubmit={(result: any) => {
              fetchMainData();
              setCurrentAddNewPaneEntity({
                schema: schema,
                row: {},
                key: undefined,
              });
              setQuery('progress');
              timerRef.current = setTimeout(() => {
                setQuery('success');
              }, 2000);
              if (refreshMainTable) {
                refreshMainTable();
              }
              return true;
            }}
            propertiesFromTable={
              currentAddNewPaneEntity?.row ? currentAddNewPaneEntity?.row : {}
            }
            row={
              currentAddNewPaneEntity?.row ? currentAddNewPaneEntity?.row : {}
            }
            onClose={(value) => {
              setCurrentAddNewPaneEntity({
                schema: schema,
                row: {},
                key: undefined,
              });
            }}
          />
        </>
      )}

      <Dialog
        open={openConfirmDialog}
        onClose={() => {
          setOpenConfirmDialog(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {'Are you sure you want to delete it?'}
        </DialogTitle>
        <DialogActions>
          <MUIButton
            onClick={() => {
              setOpenConfirmDialog(false);
            }}
          >
            Cancel
          </MUIButton>
          <MUIButton
            variant={'contained'}
            color={'error'}
            onClick={() => {
              const deletedRow = openConfirmDialog?.row;
              openConfirmDialog?.schema.client
                .delete(deletedRow.id as number, { id: deletedRow.id })
                .then((res: any) => {

                  fetchMainData();

                  toast.success(`${isAdmin?'Successfully deleted':'Sent for Approval'}: ${deletedRow?.name ? deletedRow?.name : deletedRow?.id }`);
                  setOpenConfirmDialog(false);

                  setQuery('progress');
                  timerRef.current = setTimeout(() => {
                    setQuery('success');
                  }, 2000);
                })
                .catch((e: any) => {
                  toast.success( `Error: Unable to delete Accommodation: ${deletedRow?.name ? deletedRow?.name : deletedRow?.id}`);
                });
            }}
            autoFocus
          >
            Delete
          </MUIButton>
        </DialogActions>
      </Dialog>

      {openedSlidingPane?.row?.id && (
        <SlidingPaneEdit
          schema={openedSlidingPane?.schema}
          id={openedSlidingPane?.row?.id}
          onSubmit={() => {
            setOpenedSlidingPane({});
            fetchMainData();

            setQuery('progress');
            timerRef.current = setTimeout(() => {
              setQuery('success');
            }, 1000);
          }}
          propertiesFromTable={
            openedSlidingPane?.row ? openedSlidingPane?.row : {}
          }
          onClose={(value) => {
            setOpenedSlidingPane({});
            fetchMainData();

            setQuery('progress');
            timerRef.current = setTimeout(() => {
              setQuery('success');
            }, 1000);
          }}
        />
      )}
    </>
  );
};
